import type { ReactNode } from "react";
import { cn } from "./cn.js";
import { StatusDot } from "./StatusDot.js";

/** Compact status chip — labels for overlay state, event types, counts.
 *  Pass `dot` to prefix a matching StatusDot (e.g. a live "ON AIR" badge). */
type Tone = "neutral" | "live" | "success" | "warning" | "accent";

type Props = {
  tone?: Tone;
  dot?: boolean;
  pulse?: boolean;
  className?: string;
  children: ReactNode;
};

const toneClasses: Record<Tone, string> = {
  neutral: "bg-surface-elevated text-ink-soft border border-border",
  live: "bg-danger/15 text-danger-strong border border-danger/30",
  success: "bg-success/15 text-success border border-success/30",
  warning: "bg-warning/15 text-warning border border-warning/30",
  accent: "bg-accent/15 text-accent-strong border border-accent/30",
};

const dotTone: Record<Tone, "live" | "success" | "warning" | "idle"> = {
  neutral: "idle",
  live: "live",
  success: "success",
  warning: "warning",
  accent: "idle",
};

export function Badge({
  tone = "neutral",
  dot = false,
  pulse = false,
  className,
  children,
}: Props) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5",
        "text-[11px] font-semibold uppercase tracking-[0.04em] leading-none",
        toneClasses[tone],
        className,
      )}
    >
      {dot && <StatusDot tone={dotTone[tone]} pulse={pulse} />}
      {children}
    </span>
  );
}
